<template lang="pug">
.scan-page
    //- Tab-Leiste
    .scan-tab-bar
        button.scan-tab-btn(:class="{ active: activeTab === 'scanner' }" @click="switchTab('scanner')")
            i.el-icon-fa-qrcode.fas
            |  {{ $t('Scanner') }}
        button.scan-tab-btn(:class="{ active: activeTab === 'liste' }" @click="switchTab('liste')")
            i.el-icon-fa-list.fas
            |  {{ $t('Liste') }}
            span.tab-count(v-if="totalCount > 0") {{ scannedCount }}/{{ totalCount }}
        button.scan-tab-gear(:class="{ active: showOptions }" :title="$t('Optionen')" @click="showOptions = !showOptions")
            i.el-icon-setting

    //- Optionen (Zahnrad)
    transition(name="slide-decent-vertical")
        .scan-options(v-if="showOptions")
            span.scan-options-label {{ $t('Auto weiter nach') }}
            .scan-options-chips
                button.scan-opt-chip(
                    v-for="s in [1, 2, 3, 5, 10]"
                    :key="s"
                    :class="{ active: autoContinueDelay === s }"
                    @click="autoContinueDelay = s"
                ) {{ s }} s

    //- ── SCANNER TAB ─────────────────────────────────────
    template(v-if="activeTab === 'scanner'")
        //- Kamera-Fehler
        .scan-unsupported(v-if="unsupported")
            el-result(
                icon="error"
                :title="$t('Kamera nicht verfügbar')"
                :sub-title="errorMessage"
            )
                el-button(slot="extra" type="primary" round @click="initScanner")
                    | {{ $t('Erneut versuchen') }}

        //- Scanner aktiv
        .scan-viewport(v-show="!scanResult && !unsupported")
            .video-wrapper
                #qr-video-container
                .scan-overlay
                    .scan-frame
                //- Kamera-Steuerung (nur wenn vom Geraet unterstuetzt)
                .camera-controls(v-if="torchSupported || zoomSupported")
                    button.cam-btn(
                        v-if="torchSupported"
                        :class="{ active: torchOn }"
                        :title="$t('Taschenlampe')"
                        @click="toggleTorch"
                    )
                        i.el-icon-fa-bolt.fas
                    template(v-if="zoomSupported")
                        button.cam-btn(
                            :disabled="zoom <= zoomMin"
                            :title="$t('Verkleinern')"
                            @click="zoomBy(-1)"
                        )
                            i.el-icon-fa-search-minus.fas
                        button.cam-btn(
                            :disabled="zoom >= zoomMax"
                            :title="$t('Vergroessern')"
                            @click="zoomBy(1)"
                        )
                            i.el-icon-fa-search-plus.fas
            p.scan-hint
                i.el-icon-fa-qrcode.fas
                |  {{ $t('Kamera auf QR-Code richten') }}

        //- Footer: Einstellungen (Automatisch weiter + Sounds)
        .scan-footer(v-show="!scanResult && !unsupported")
            .scan-toggle(:class="{ active: autoContinue }" @click="autoContinue = !autoContinue")
                i.el-icon-fa-bolt.fas.scan-toggle-icon
                span.scan-toggle-label {{ $t('Auto weiter') }}
                el-switch.scan-toggle-switch(v-model="autoContinue")
            .scan-toggle(:class="{ active: soundEnabled }" @click="soundEnabled = !soundEnabled")
                i.el-icon-fa-volume-high.fas.scan-toggle-icon
                span.scan-toggle-label {{ $t('Ton') }}
                el-switch.scan-toggle-switch(v-model="soundEnabled")

        //- Ergebnis
        .scan-result(v-if="scanResult" :class="scanResult.variant")
            //- Erfolg: ganzflächige grüne Bestätigung
            .scan-success(v-if="scanResult.type === 'success'")
                .success-check
                    i.el-icon-fa-check.fas
                p.success-title {{ $t('Erfolgreich gescannt') }}
                p.success-name(v-if="scanResult.booking") {{ bookerName(scanResult.booking) }}
                p.success-slot(v-if="scanResult.booking && slotTime(scanResult.booking)")
                    i.el-icon-fa-clock.far
                    |  {{ slotTime(scanResult.booking) }}
                el-button.success-continue(round @click="reset")
                    i.el-icon-fa-redo.fas
                    |  {{ $t('Weiter scannen') }}

            //- Bereits gescannt (amber) bzw. Fehler/ungültig (rot)
            .scan-fail(v-else)
                .fail-icon
                    i.fas(:class="scanResult.variant === 'warning' ? 'el-icon-fa-clock' : 'el-icon-fa-xmark'")
                p.fail-title {{ scanResult.title }}
                p.fail-name(v-if="scanResult.booking") {{ bookerName(scanResult.booking) }}
                p.fail-time(v-if="scanResult.scannedAt")
                    i.el-icon-fa-clock.far
                    |  {{ $t('Gescannt um') }} {{ formatScannedAt(scanResult.scannedAt) }}
                p.fail-msg(v-if="scanResult.message") {{ scanResult.message }}
                //- Serienticket ausserhalb jedes Termin-Zeitfensters: der Server kann nicht
                //- wissen, welche Tür das ist - also entscheidet die Person am Eingang.
                .session-picker(v-if="pendingSessions.length")
                    el-button.session-option(
                        v-for="session in pendingSessions"
                        :key="session.id"
                        :disabled="session.scanned"
                        round
                        @click="scanSession(session)"
                    )
                        span {{ sessionLabel(session) }}
                        span.session-done(v-if="session.scanned")  ✓
                el-button.fail-continue(round @click="reset")
                    i.el-icon-fa-redo.fas
                    |  {{ $t('Weiter scannen') }}

    //- ── LISTE TAB ───────────────────────────────────────
    template(v-else-if="activeTab === 'liste'")
        .booking-list-view
            //- Statistik + Suche
            .list-header
                .list-stats
                    span.stats-scanned {{ scannedCount }}
                    span.stats-sep  /
                    span.stats-total {{ totalCount }}
                    span.stats-label  {{ $t('gescannt') }}
                el-input.list-search(
                    v-model="search"
                    :placeholder="$t('Name oder E-Mail suchen')"
                    prefix-icon="el-icon-search"
                    size="small"
                    clearable
                )

            //- Ladeindikator
            .list-loading(v-if="listLoading")
                el-skeleton(:rows="5" animated)

            //- Leer
            el-empty(
                v-else-if="!filteredBookings.length"
                :description="search ? $t('Keine Treffer') : $t('Keine Anmeldungen')"
            )

            //- Buchungen
            .booking-items(v-else)
                .booking-item(
                    v-for="b in filteredBookings"
                    :key="b.id"
                    :class="{ scanned: b.scanned }"
                )
                    .booking-avatar(:class="{ scanned: b.scanned }")
                        | {{ initials(b) }}
                    .booking-info
                        .booking-name {{ bookerName(b) }}
                        .booking-meta {{ b.booker && b.booker.email || '' }}
                        .booking-slot(v-if="slotTime(b)") {{ slotTime(b) }}
                    .booking-action
                        template(v-if="b.scanned")
                            el-tag(type="success" size="mini" effect="dark") {{ $t('Gescannt') }}
                            .scanned-time(v-if="b.scanned_at") {{ formatScannedAt(b.scanned_at) }}
                        .action-buttons(v-else)
                            el-button.resend-mail-btn(
                                size="mini"
                                type="info"
                                :loading="resendingId === b.id"
                                :disabled="resendingId === b.id"
                                :title="$t('Bestätigungsmail erneut senden')"
                                @click="resendMail(b)"
                            )
                                i.el-icon-fa-envelope.far(v-if="resendingId !== b.id")
                            el-button(
                                size="mini"
                                type="primary"
                                :loading="listScanningId === b.id"
                                @click="scanFromList(b)"
                            )
                                i.el-icon-fa-check.fas(v-if="listScanningId !== b.id")
                                |  {{ $t('Aktivieren') }}

            //- Manuelle Eingabe (auch im Liste-Tab)
            .manual-entry
                .manual-divider
                    span {{ $t('oder manuell eingeben') }}
                .manual-input-row
                    el-input(
                        v-model="manualToken"
                        :placeholder="$t('Code eingeben z.B. KDOUD2X')"
                        size="medium"
                        :disabled="manualScanning"
                        @keyup.enter.native="submitManual"
                    )
                        template(slot="append")
                            el-button(
                                :loading="manualScanning"
                                :disabled="!manualToken.trim()"
                                @click="submitManual"
                            )
                                i.el-icon-fa-check.fas
</template>

<i18n>
{
  "de": {
    "Scanner": "Scanner",
    "Liste": "Liste",
    "Kamera nicht verfügbar": "Kamera nicht verfügbar",
    "Erneut versuchen": "Erneut versuchen",
    "Kamera auf QR-Code richten": "Kamera auf QR-Code richten",
    "Auto weiter": "Auto weiter",
    "Ton": "Ton",
    "Optionen": "Optionen",
    "Auto weiter nach": "Auto weiter nach",
    "oder manuell eingeben": "oder manuell eingeben",
    "Code eingeben z.B. KDOUD2X": "Code eingeben z.B. KDOUD2X",
    "Name": "Name",
    "E-Mail": "E-Mail",
    "Slot": "Slot",
    "Status": "Status",
    "Gescannt": "Gescannt",
    "Weiter scannen": "Weiter scannen",
    "Buchung erfolgreich gescannt": "Buchung erfolgreich gescannt",
    "Erfolgreich gescannt": "Erfolgreich gescannt",
    "Ungültiger QR-Code": "Ungültiger QR-Code",
    "QR-Code enthält keinen gültigen Scan-Token": "QR-Code enthält keinen gültigen Scan-Token",
    "Scan fehlgeschlagen": "Scan fehlgeschlagen",
    "Bereits gescannt": "Bereits gescannt!",
    "Welcher Termin?": "Welcher Termin?",
    "Dieses Ticket gilt für mehrere Termine.": "Dieses Ticket gilt für mehrere Termine.",
    "Dieser QR-Code wurde bereits verwendet": "Dieser QR-Code wurde bereits verwendet.",
    "Gescannt um": "Gescannt um",
    "gescannt": "gescannt",
    "Name oder E-Mail suchen": "Name oder E-Mail suchen",
    "Keine Treffer": "Keine Treffer",
    "Keine Anmeldungen": "Keine Anmeldungen",
    "Aktivieren": "Aktivieren",
    "Bestätigungsmail erneut senden": "Bestätigungsmail erneut senden",
    "Bestätigungsmail wurde gesendet": "Bestätigungsmail wurde gesendet",
    "Senden fehlgeschlagen": "Senden fehlgeschlagen",
    "Taschenlampe": "Taschenlampe",
    "Taschenlampe nicht verfügbar": "Taschenlampe nicht verfügbar",
    "Verkleinern": "Verkleinern",
    "Vergroessern": "Vergrößern"
  }
}
</i18n>

<script>
import _ from 'lodash'
import { useEventManagerBookingStore, useSetupStore } from '@/stores'
import { timestamp2date } from '@/filters'
import EventApi from '@/extensions/eventManager/store/api/event'
import successSound from '@/assets/sounds/true.mp3'
import errorSound from '@/assets/sounds/false.mp3'

export default {
    name: 'ScanScanner',
    data() {
        return {
            activeTab: 'scanner',
            scanResult: null,
            scanning: false,
            unsupported: false,
            errorMessage: null,
            html5QrCode: null,
            // Gesetzt, wenn ein Serienticket keinem einzelnen Termin zugeordnet werden konnte.
            pendingToken: null,
            pendingSessions: [],
            event: null,
            listLoading: false,
            // Scanner
            manualToken: '',
            manualScanning: false,
            // Kamera-Steuerung
            torchSupported: false,
            torchOn: false,
            zoomSupported: false,
            zoom: 1,
            zoomMin: 1,
            zoomMax: 1,
            zoomStep: 0.1,
            // Liste
            search: '',
            listScanningId: null,
            resendingId: null,
            // Nach erfolgreichem Scan automatisch weiter (Standard: aus)
            autoContinue: false,
            autoContinueTimer: null,
            autoContinueDelay: 2, // Sekunden, einstellbar über das Zahnrad
            showOptions: false,
            // Ton bei Scan (Standard: an)
            soundEnabled: true,
        }
    },
    computed: {
        bookingStore() { return useEventManagerBookingStore() },
        eventId() { return this.$route.params.id },
        cmsId() { return useSetupStore().activeCms?.id },
        allBookings() {
            return (this.event?.slots || []).flatMap(s => {
                return (s.bookings || []).map(b => ({ ...b, slot: _.omit(s, ['bookings']) }))
            })
        },
        scannedCount() { return this.allBookings.filter(b => b.scanned).length },
        totalCount() { return this.allBookings.length },
        filteredBookings() {
            let list = [...this.allBookings]
            if (this.search.trim()) {
                const q = this.search.toLowerCase()
                list = list.filter(b => {
                    const name = this.bookerName(b).toLowerCase()
                    const email = (b.booker?.email || '').toLowerCase()
                    const token = (b.qr_token || '').toLowerCase()
                    return name.includes(q) || email.includes(q) || token.includes(q)
                })
            }
            // Nicht gescannte zuerst
            return list.sort((a, b) => (a.scanned === b.scanned ? 0 : a.scanned ? 1 : -1))
        },
    },
    mounted() {
        this.initAudio()
        this.loadEvent()
        this.initScanner()
    },
    beforeDestroy() {
        if (this._unlockAudio) {
            document.removeEventListener('touchend', this._unlockAudio)
            document.removeEventListener('click', this._unlockAudio)
        }
        if (this.audioCtx && this.audioCtx.state !== 'closed') {
            try { this.audioCtx.close() } catch (e) { /* ignore */ }
        }
        if (this.autoContinueTimer) clearTimeout(this.autoContinueTimer)
        this.stopCamera()
        this.$root.$emit('scan:reset')
    },
    methods: {
        async switchTab(tab) {
            if (this.activeTab === tab) return
            this.activeTab = tab
            if (tab === 'liste') {
                this.stopCamera()
            } else if (tab === 'scanner') {
                if (!this.scanResult) {
                    await this.$nextTick()
                    this.initScanner()
                }
            }
        },

        async loadEvent() {
            this.listLoading = true
            try {
                const events = await EventApi.fetchList(this.cmsId)
                this.event = (events || []).find(e => String(e.id) === String(this.eventId)) || null
                this.emitInfo()
            } catch {
                // Event konnte nicht geladen werden -> leere Ansicht, kein Abbruch
            }
            this.listLoading = false
        },

        emitInfo() {
            this.$root.$emit('scan:event', {
                name: this.event?.name || '',
                scanned: this.scannedCount,
                total: this.totalCount,
            })
        },

        async initScanner() {
            this.unsupported = false
            this.errorMessage = null
            this.scanResult = null
            this.scanning = false

            try {
                // Eventuell noch laufende/nicht aufgeräumte Instanz sauber beenden,
                // sonst hält sie die Kamera und die Neu-Initialisierung schlägt fehl
                // (z.B. nach "Auto weiter").
                await this.stopCamera()

                const { Html5Qrcode } = await import('html5-qrcode')
                const devices = await Html5Qrcode.getCameras()
                if (!devices?.length) throw new Error('Keine Kamera gefunden')

                const cam = devices.find(d => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1]
                this.html5QrCode = new Html5Qrcode('qr-video-container', { verbose: false })

                await this.html5QrCode.start(
                    cam.id,
                    { fps: 10, qrbox: { width: 250, height: 250 } },
                    (text) => this.onScanSuccess(text),
                    () => {}
                )

                this.setupCameraControls()
            } catch (e) {
                this.unsupported = true
                this.errorMessage = e.message || this.$t('Kamera nicht verfügbar')
            }
        },

        // Torch/Zoom-Faehigkeiten der laufenden Kamera ermitteln (best effort)
        setupCameraControls() {
            this.torchSupported = false
            this.zoomSupported = false
            try {
                const caps = this.html5QrCode.getRunningTrackCameraCapabilities()

                const torch = caps.torchFeature()
                if (torch.isSupported()) {
                    this.torchSupported = true
                    this.torchOn = !!torch.value()
                }

                const zoom = caps.zoomFeature()
                if (zoom.isSupported()) {
                    this.zoomSupported = true
                    this.zoomMin = zoom.min() ?? 1
                    this.zoomMax = zoom.max() ?? 1
                    this.zoomStep = zoom.step() || 0.1
                    this.zoom = zoom.value() ?? this.zoomMin
                }
            } catch (e) {
                // Capabilities nicht verfügbar (z.B. nicht im running state) -> Buttons bleiben aus
            }
        },

        async toggleTorch() {
            if (!this.torchSupported || !this.html5QrCode) return
            try {
                const torch = this.html5QrCode.getRunningTrackCameraCapabilities().torchFeature()
                await torch.apply(!this.torchOn)
                this.torchOn = !this.torchOn
            } catch (e) {
                this.$message({ type: 'error', message: this.$t('Taschenlampe nicht verfügbar') })
            }
        },

        // Zoom in groesseren Schritten (ca. 1/10 des Bereichs) hoch-/runterregeln
        async zoomBy(direction) {
            if (!this.zoomSupported || !this.html5QrCode) return
            const stride = Math.max(this.zoomStep, (this.zoomMax - this.zoomMin) / 10)
            const target = Math.min(this.zoomMax, Math.max(this.zoomMin, this.zoom + direction * stride))
            if (target === this.zoom) return
            try {
                await this.html5QrCode.getRunningTrackCameraCapabilities().zoomFeature().apply(target)
                this.zoom = target
            } catch (e) {
                // Zoom konnte nicht angewendet werden
            }
        },

        onScanSuccess(decodedText) {
            if (this.scanning) return
            this.scanning = true
            this.stopCamera()

            const token = this.extractToken(decodedText)
            if (!token) {
                this.scanResult = {
                    type: 'error',
                    variant: 'error',
                    title: this.$t('Ungültiger QR-Code'),
                    message: this.$t('QR-Code enthält keinen gültigen Scan-Token'),
                    booking: null,
                }
                this.playSound('error')
                this.scanning = false
                return
            }

            this.processToken(token)
                .finally(() => { this.scanning = false })
        },

        // Manuelle Eingabe gibt es nur im Liste-Tab -> Toast-Feedback + Liste aktualisieren
        submitManual() {
            const token = this.manualToken.trim()
            if (!token || this.manualScanning) return
            this.manualScanning = true

            this.bookingStore.scanBooking(token)
                .then(updated => {
                    this.manualToken = ''
                    this.updateLocalBooking(updated)
                    this.$message({ type: 'success', message: this.$t('Buchung erfolgreich gescannt') })
                })
                .catch(e => {
                    const msg = _.get(e, 'graphQLErrors[0].message') || e.message || ''
                    const status = _.get(e, 'status')
                    const alreadyScanned = status === 'already-scanned'
                        || msg.toLowerCase().includes('already been scanned')

                    // Serienticket: die Termin-Auswahl liegt im Scan-Tab, braucht aber keine
                    // Kamera - also dorthin wechseln und auswählen lassen, statt die Person am
                    // Eingang mit einem Ticket stehen zu lassen, das sie nicht einlesen kann.
                    if (status === 'ambiguous') {
                        this.manualToken = ''
                        this.pendingToken = token
                        this.pendingSessions = _.get(e, 'sessions') || []
                        this.scanResult = {
                            type: 'error',
                            variant: 'warning',
                            title: this.$t('Welcher Termin?'),
                            message: this.$t('Dieses Ticket gilt für mehrere Termine.'),
                            booking: null,
                            scannedAt: null,
                        }
                        this.switchTab('scanner')
                        return
                    }

                    this.$message({
                        type: alreadyScanned ? 'warning' : 'error',
                        message: status === 'not-paid'
                            ? this.$t('Diese Bestellung ist noch nicht bezahlt')
                            : (alreadyScanned ? this.$t('Dieser QR-Code wurde bereits verwendet') : (msg || this.$t('Scan fehlgeschlagen'))),
                    })
                })
                .finally(() => { this.manualScanning = false })
        },

        processToken(token) {
            return this.bookingStore.scanBooking(token)
                .then(booking => {
                    this.manualToken = ''
                    this.scanResult = {
                        type: 'success',
                        variant: 'success',
                        title: this.$t('Erfolgreich gescannt'),
                        booking,
                    }
                    this.updateLocalBooking(booking)
                    this.playSound('success')
                    // Nur bei Erfolg automatisch weiter (Fehler/rot bleiben stehen)
                    if (this.autoContinue) {
                        this.autoContinueTimer = setTimeout(() => this.reset(), this.autoContinueDelay * 1000)
                    }
                })
                .catch(e => {
                    const msg = _.get(e, 'graphQLErrors[0].message') || e.message || ''
                    const status = _.get(e, 'status')
                    const alreadyScanned = status === 'already-scanned'
                        || msg.toLowerCase().includes('already been scanned')

                    // Serienticket: kein Termin läuft gerade, also die Termine zur Auswahl zeigen.
                    if (status === 'ambiguous') {
                        this.pendingToken = token
                        this.pendingSessions = _.get(e, 'sessions') || []
                        this.scanResult = {
                            type: 'error',
                            variant: 'warning',
                            title: this.$t('Welcher Termin?'),
                            message: this.$t('Dieses Ticket gilt für mehrere Termine.'),
                            booking: null,
                            scannedAt: null,
                        }
                        this.playSound('error')
                        return
                    }

                    // Das PDF entsteht schon beim Bestellen, die Gültigkeit erst mit der Zahlung.
                    const notPaid = status === 'not-paid'

                    let title = this.$t('Scan fehlgeschlagen')
                    if (alreadyScanned) title = this.$t('Bereits gescannt')
                    if (notPaid) title = this.$t('Noch nicht bezahlt')

                    let message = msg
                    if (alreadyScanned) message = this.$t('Dieser QR-Code wurde bereits verwendet')
                    if (notPaid) message = this.$t('Diese Bestellung ist noch nicht bezahlt')

                    // Der Fehler liefert keinen Timestamp -> Scan-Zeit aus der lokal
                    // geladenen Buchungsliste (per Token) holen, um sie anzuzeigen.
                    const existing = alreadyScanned ? this.allBookings.find(b => b.qr_token === token) : null
                    this.scanResult = {
                        type: 'error',
                        variant: alreadyScanned ? 'warning' : 'error',
                        title,
                        message,
                        booking: existing || null,
                        scannedAt: existing ? existing.scanned_at : null,
                    }
                    this.playSound('error')
                })
        },

        sessionLabel(session) {
            const startAt = _.get(session, 'slot.start_at')
            if (!startAt) return '-'
            return timestamp2date(startAt, 'dd.MM.yyyy, HH:mm')
        },

        // Die Person am Eingang hat den Termin gewählt - gleiche Mutation, diesmal mit Termin.
        scanSession(session) {
            const token = this.pendingToken
            this.pendingSessions = []

            return this.bookingStore.scanBooking(token, _.get(session, 'slot.id'))
                .then(booking => {
                    this.scanResult = {
                        type: 'success',
                        variant: 'success',
                        title: this.$t('Erfolgreich gescannt'),
                        booking,
                    }
                    this.updateLocalBooking(booking)
                    this.playSound('success')
                })
                .catch(e => {
                    this.scanResult = {
                        type: 'error',
                        variant: 'error',
                        title: this.$t('Scan fehlgeschlagen'),
                        message: _.get(e, 'message') || '',
                        booking: null,
                        scannedAt: null,
                    }
                    this.playSound('error')
                })
        },

        scanFromList(booking) {
            if (this.listScanningId) return
            this.listScanningId = booking.id

            // Aus der Liste heraus ist der Termin bekannt - also mitschicken, damit ein
            // Serienticket nicht nachfragen muss.
            this.bookingStore.scanBooking(booking.qr_token, _.get(booking, 'slot.id'))
                .then(updated => { this.updateLocalBooking(updated) })
                .catch(e => {
                    const msg = _.get(e, 'graphQLErrors[0].message') || e.message || ''
                    this.$message({ type: 'error', message: msg || this.$t('Scan fehlgeschlagen') })
                })
                .finally(() => { this.listScanningId = null })
        },

        // Bestätigungsmail inkl. QR-Code erneut an den Bucher senden.
        resendMail(booking) {
            if (this.resendingId) return
            this.resendingId = booking.id

            this.bookingStore.resendConfirmation(booking.id)
                .then(() => {
                    this.$message({ type: 'success', message: this.$t('Bestätigungsmail wurde gesendet') })
                })
                .catch(e => {
                    const msg = _.get(e, 'graphQLErrors[0].message') || e.message || ''
                    this.$message({ type: 'error', message: msg || this.$t('Senden fehlgeschlagen') })
                })
                .finally(() => { this.resendingId = null })
        },

        updateLocalBooking(booking) {
            if (!this.event) return
            this.event.slots?.forEach(slot => {
                const b = slot.bookings?.find(b => b.id === booking.id)
                if (b) {
                    b.scanned = true
                    b.scanned_at = booking.scanned_at
                }
            })
            this.emitInfo()
        },

        extractToken(text) {
            try {
                const url = new URL(text)
                // Signierte URL (?token=...) hat Vorrang, Fallback auf Legacy ?scan=...
                return url.searchParams.get('token') || url.searchParams.get('scan') || null
            } catch {
                if (text && text.trim().length >= 6 && !text.includes(' ')) return text.trim()
                return null
            }
        },

        async stopCamera() {
            this.torchSupported = false
            this.zoomSupported = false
            this.torchOn = false
            if (this.html5QrCode) {
                // Referenz zuerst lösen, damit keine Re-Entrancy passiert
                const instance = this.html5QrCode
                this.html5QrCode = null
                try {
                    // Unbedingt stoppen (Kamera-Stream freigeben); wirft, wenn nicht aktiv
                    await instance.stop()
                } catch {
                    // nicht aktiv / bereits gestoppt -> ignorieren
                }
                try {
                    // Container leeren, damit die nächste Initialisierung sauber rendert
                    instance.clear()
                } catch {
                    // ignorieren
                }
            }
        },

        async reset() {
            if (this.autoContinueTimer) {
                clearTimeout(this.autoContinueTimer)
                this.autoContinueTimer = null
            }
            this.scanResult = null
            this.scanning = false
            this.pendingToken = null
            this.pendingSessions = []
            await this.$nextTick()
            this.initScanner()
        },

        // iOS: Audio-Elemente innerhalb einer User-Geste "entsperren" (kurz
        // anspielen + sofort pausieren), danach ist programmatisches play() erlaubt.
        initAudio() {
            const AudioCtx = window.AudioContext || window.webkitAudioContext
            if (!AudioCtx) return
            try {
                this.audioCtx = new AudioCtx()
            } catch (e) {
                return
            }
            this.audioBuffers = { success: null, error: null }
            this.loadAudioBuffer(successSound, 'success')
            this.loadAudioBuffer(errorSound, 'error')
            // Web Audio ist erst nach einer User-Geste erlaubt. Bei der ersten Berührung
            // den Context lautlos per resume() freischalten (kein Vorspielen -> kein "Blip").
            // Funktioniert so auf iOS und Android.
            this._unlockAudio = () => {
                if (this.audioCtx) {
                    if (this.audioCtx.state === 'suspended') this.audioCtx.resume().catch(() => {})
                    // iOS: resume() allein reicht nicht -> innerhalb der Geste einmal einen
                    // stillen Buffer starten, sonst bleibt die Ausgabe stumm.
                    try {
                        const silent = this.audioCtx.createBuffer(1, 1, 22050)
                        const source = this.audioCtx.createBufferSource()
                        source.buffer = silent
                        source.connect(this.audioCtx.destination)
                        source.start(0)
                    } catch (e) {
                        // ignorieren
                    }
                }
                document.removeEventListener('touchend', this._unlockAudio)
                document.removeEventListener('click', this._unlockAudio)
            }
            document.addEventListener('touchend', this._unlockAudio, { passive: true })
            document.addEventListener('click', this._unlockAudio)
        },
        async loadAudioBuffer(url, key) {
            if (!this.audioCtx) return
            try {
                const response = await fetch(url)
                const arrayBuffer = await response.arrayBuffer()
                this.audioBuffers[key] = await this.audioCtx.decodeAudioData(arrayBuffer)
            } catch (e) {
                // Dekodierung fehlgeschlagen -> Ton für diesen Typ nicht verfügbar
            }
        },

        playSound(type) {
            if (!this.soundEnabled || !this.audioCtx) return
            const buffer = _.get(this.audioBuffers, type === 'success' ? 'success' : 'error')
            if (!buffer) return
            try {
                if (this.audioCtx.state === 'suspended') this.audioCtx.resume().catch(() => {})
                const source = this.audioCtx.createBufferSource()
                source.buffer = buffer
                source.connect(this.audioCtx.destination)
                source.start(0)
            } catch (e) {
                // ignorieren
            }
        },

        formatScannedAt(ts) {
            return timestamp2date(ts, 'dd.MM. HH:mm') + ' Uhr'
        },
        bookerName(booking) {
            const b = booking.booker
            if (!b) return '-'
            const first = b.first_name && b.first_name !== '-' ? b.first_name : ''
            const last = b.last_name && b.last_name !== '-' ? b.last_name : ''
            return [first, last].filter(Boolean).join(' ') || b.email || '-'
        },

        initials(booking) {
            const b = booking.booker
            if (!b) return '?'
            const first = b.first_name && b.first_name !== '-' ? b.first_name[0] : ''
            const last = b.last_name && b.last_name !== '-' ? b.last_name[0] : ''
            return (first + last).toUpperCase() || (b.email?.[0] || '?').toUpperCase()
        },

        slotTime(booking) {
            const start = _.get(booking, 'slot.start_at')
            const end = _.get(booking, 'slot.end_at')
            if (!start) return null
            return `${timestamp2date(start, 'dd.MM.yyyy HH:mm')} – ${timestamp2date(end, 'HH:mm')} Uhr`
        },
    },
}
</script>

<style lang="sass" scoped>
.scan-page
    flex: 1
    min-height: 0
    display: flex
    flex-direction: column
    overflow: hidden
    background: #0d0d0d

// ── Tab-Leiste ──────────────────────────────────────────
.scan-tab-bar
    flex-shrink: 0
    display: flex
    background: #111
    border-bottom: 1px solid #222

.scan-tab-btn
    flex: 1
    padding: 12px 8px
    background: none
    border: none
    color: #666
    font-size: 13px
    font-weight: 500
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    gap: 6px
    transition: color .2s, border-color .2s
    border-bottom: 2px solid transparent
    margin-bottom: -1px

    &.active
        color: $--color-primary
        border-bottom-color: $--color-primary

    .tab-count
        background: #2a2a2a
        color: #aaa
        font-size: 11px
        padding: 1px 6px
        border-radius: 10px

.scan-tab-gear
    flex: 0 0 auto
    width: 48px
    background: none
    border: none
    border-bottom: 2px solid transparent
    margin-bottom: -1px
    color: #666
    font-size: 17px
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    transition: color .2s

    &:active
        color: #aaa

    &.active
        color: $--color-primary

// ── Optionen (Zahnrad) ──────────────────────────────────
.scan-options
    flex-shrink: 0
    display: flex
    align-items: center
    gap: 12px
    padding: 10px 16px
    background: #141414
    border-bottom: 1px solid #222

    .scan-options-label
        font-size: 12px
        color: #9aa0a6
        white-space: nowrap

    .scan-options-chips
        display: flex
        flex-wrap: wrap
        gap: 6px

        .scan-opt-chip
            min-width: 42px
            padding: 6px 10px
            border-radius: 4px
            background: #1e1e1e
            border: 1px solid #333
            color: #b0b3b8
            font-size: 13px
            font-weight: 600
            cursor: pointer
            transition: background .15s, border-color .15s, color .15s

            &.active
                background: rgba(64, 158, 255, 0.15)
                border-color: $--color-primary
                color: $--color-primary

// ── Scanner Tab ─────────────────────────────────────────
.scan-viewport
    flex: 1
    min-height: 0
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    padding: 16px
    overflow: hidden

    .video-wrapper
        position: relative
        width: 100%
        max-width: 380px
        border-radius: 16px
        overflow: hidden
        background: #000
        box-shadow: 0 8px 32px rgba(0,0,0,0.2)
        line-height: 0

        #qr-video-container
            width: 100%
            ::v-deep
                // Video bestimmt die Hoehe des Wrappers (kein fixer aspect-ratio),
                // damit auf allen Geraeten kein schwarzer Balken entsteht. iOS Safari
                // ignoriert object-fit/cover am Kamera-Video haeufig, daher dieser Weg.
                video
                    display: block !important
                    width: 100% !important
                    height: auto !important
                #qr-video-container__dashboard
                    display: none !important
                // eingebautes graues Overlay (Abdunkelung + Eck-Marker) ausblenden,
                // damit nur die eigene blaue .scan-frame als Markierung sichtbar ist
                #qr-shaded-region
                    display: none !important

        .scan-overlay
            position: absolute
            inset: 0
            display: flex
            align-items: center
            justify-content: center
            pointer-events: none

            .scan-frame
                width: 220px
                height: 220px
                border: 3px solid $--color-primary
                border-radius: 14px
                box-shadow: 0 0 0 9999px rgba(0,0,0,0.45)
                animation: pulse 2s ease-in-out infinite

        .camera-controls
            position: absolute
            right: 10px
            bottom: 10px
            z-index: 2
            display: flex
            gap: 8px
            line-height: normal

            .cam-btn
                width: 40px
                height: 40px
                border-radius: 50%
                border: none
                background: rgba(0,0,0,0.5)
                backdrop-filter: blur(4px)
                color: #fff
                font-size: 16px
                cursor: pointer
                display: flex
                align-items: center
                justify-content: center
                transition: background .15s, color .15s, opacity .15s

                &:active
                    background: rgba(0,0,0,0.7)

                &.active
                    background: $--color-primary
                    color: #fff

                &:disabled
                    opacity: 0.35
                    cursor: default

    .scan-hint
        flex-shrink: 0
        margin-top: 16px
        color: #909399
        font-size: 14px
        text-align: center
        i
            color: $--color-primary
            margin-right: 6px


// ── Footer: Einstellungen (Toggles) ────────────────────
// Optik konsistent mit dem Liste-Footer (Trennlinie + #1a1a1a/#333 Flächen).
.scan-footer
    flex-shrink: 0
    padding: 12px 16px
    border-top: 1px solid #1e1e1e
    display: flex
    flex-wrap: nowrap
    align-items: center
    justify-content: center
    gap: 12px

    .scan-toggle
        flex: 1
        min-width: 0
        max-width: 200px
        display: flex
        align-items: center
        gap: 8px
        padding: 8px 11px
        border-radius: 4px
        background: #1a1a1a
        border: 1px solid #333
        cursor: pointer
        user-select: none
        transition: border-color .2s, background .2s

        &.active
            border-color: $--color-primary
            background: rgba(64, 158, 255, 0.12)

        .scan-toggle-icon
            flex-shrink: 0
            font-size: 14px
            color: #7d818a
            transition: color .2s

        &.active .scan-toggle-icon
            color: $--color-primary

        .scan-toggle-label
            flex: 1
            min-width: 0
            font-size: 13px
            font-weight: 600
            color: #c2c6cc
            white-space: nowrap
            overflow: hidden
            text-overflow: ellipsis

        .scan-toggle-switch
            flex-shrink: 0
            pointer-events: none

// ── Manuelle Eingabe ────────────────────────────────────
.manual-entry
    flex-shrink: 0
    padding: 12px 16px

    .manual-divider
        display: flex
        align-items: center
        gap: 10px
        margin-bottom: 10px
        &::before, &::after
            content: ''
            flex: 1
            height: 1px
            background: #2a2a2a
        span
            font-size: 12px
            color: #999
            white-space: nowrap

    .manual-input-row
        ::v-deep
            .el-input__inner
                background: #1a1a1a
                border-color: #333
                color: #fff
                &::placeholder
                    color: #444
                    font-family: monospace
                    letter-spacing: 2px
            .el-input-group__append
                background: #1e3a5f
                border-color: #333
                color: $--color-primary

// ── Ergebnis / Fehler ───────────────────────────────────
.scan-result,
.scan-unsupported
    flex: 1
    display: flex
    align-items: center
    justify-content: center
    padding: 24px 16px

    ::v-deep .el-result
        padding: 20px
        &__extra
            margin-top: 20px
        &__title p
            color: #fff
        &__subtitle p
            color: #666

    .booking-card
        text-align: left
        margin-bottom: 20px
        border-radius: 10px
        width: 100%
        max-width: 360px
        ::v-deep
            &.el-card
                background: #1a1a1a
                border-color: #2a2a2a
            .el-card__body
                padding: 0
            .el-descriptions-item__label
                background: #111
                color: #666
                white-space: nowrap
                border-color: #2a2a2a
            .el-descriptions-item__content
                background: #1a1a1a
                color: #eee
                border-color: #2a2a2a
            .el-tag--success
                font-weight: 700
            .el-descriptions__body .el-descriptions__table
                border-color: #2a2a2a
            .el-descriptions--border .el-descriptions-item__cell
                border-color: #2a2a2a

    .result-error-msg
        color: #aaa
        font-size: 14px
        margin-bottom: 16px

// ── Erfolg: ganzflächig grün ────────────────────────────
// Bewusst laut & aus Distanz erkennbar (Einlass-Situation): grüner Vollflächen-
// Screen mit poppendem Häkchen und grossem Namen. Nur der FRISCHE Erfolg ist grün –
// "bereits gescannt"/Fehler bleiben rot (siehe el-result oben), um Doppel-Scans
// nicht versehentlich durchzuwinken.
.scan-result.success
    background: linear-gradient(160deg, #5cbb34 0%, #3f9e22 100%)
    animation: successFade .25s ease-out

    .scan-success
        display: flex
        flex-direction: column
        align-items: center
        text-align: center
        color: #fff
        width: 100%
        max-width: 360px
        padding: 8px

        .success-check
            width: 108px
            height: 108px
            border-radius: 50%
            background: rgba(255,255,255,0.2)
            display: flex
            align-items: center
            justify-content: center
            margin-bottom: 22px
            box-shadow: 0 0 0 8px rgba(255,255,255,0.12)
            animation: checkPop .34s cubic-bezier(.2,.9,.3,1.25) .04s both

            i
                font-size: 54px
                color: #fff

        .success-title
            margin: 0
            font-size: 14px
            font-weight: 700
            letter-spacing: 1px
            text-transform: uppercase
            opacity: .9

        .success-name
            margin: 8px 0 0
            font-size: 26px
            font-weight: 800
            line-height: 1.2
            word-break: break-word

        .success-slot
            margin: 12px 0 0
            font-size: 14px
            opacity: .92
            i
                margin-right: 6px

        .success-continue.el-button
            margin-top: 32px
            background: #fff
            border: none
            color: #2e7d18
            font-weight: 700
            padding: 12px 28px

            &:hover, &:focus
                background: #f2fff0
                color: #2e7d18

// ── Bereits gescannt / Fehler: ganzflächig rot ──────────
// Beide sind ein "Stopp": Doppel-Scan (mit Scan-Zeit) bzw. ungültiger Code/Fehler.
.scan-result.warning,
.scan-result.error
    background: linear-gradient(160deg, #f2564a 0%, #cf3526 100%)
    animation: successFade .25s ease-out

.scan-fail
    display: flex
    flex-direction: column
    align-items: center
    text-align: center
    color: #fff
    width: 100%
    max-width: 360px
    padding: 8px

    .fail-icon
        width: 108px
        height: 108px
        border-radius: 50%
        background: rgba(255,255,255,0.2)
        display: flex
        align-items: center
        justify-content: center
        margin-bottom: 22px
        box-shadow: 0 0 0 8px rgba(255,255,255,0.12)
        animation: checkPop .34s cubic-bezier(.2,.9,.3,1.25) .04s both

        i
            font-size: 54px
            color: #fff

    .fail-title
        margin: 0
        font-size: 22px
        font-weight: 800
        line-height: 1.2

    .fail-name
        margin: 10px 0 0
        font-size: 20px
        font-weight: 700
        opacity: .95
        word-break: break-word

    .fail-time
        margin: 12px 0 0
        font-size: 15px
        font-weight: 600
        opacity: .95
        i
            margin-right: 6px

    .fail-msg
        margin: 10px 0 0
        font-size: 14px
        opacity: .88

    .session-picker
        display: flex
        flex-direction: column
        align-items: stretch
        gap: 10px
        margin-top: 24px
        width: 100%
        max-width: 340px

        .session-option.el-button
            margin: 0
            background: #fff
            border: none
            font-weight: 700
            padding: 14px 20px
            color: #c0392b

            &.is-disabled
                opacity: .6

            .session-done
                font-weight: 400

    .fail-continue.el-button
        margin-top: 30px
        background: #fff
        border: none
        font-weight: 700
        padding: 12px 28px

        &:hover, &:focus
            background: #f7f7f7

.scan-result.warning .fail-continue.el-button,
.scan-result.error .fail-continue.el-button
    color: #c0392b

// ── Liste Tab ───────────────────────────────────────────
.booking-list-view
    flex: 1
    min-height: 0
    display: flex
    flex-direction: column
    overflow: hidden

.list-header
    flex-shrink: 0
    padding: 12px 16px
    background: #111
    border-bottom: 1px solid #1e1e1e
    display: flex
    align-items: center
    gap: 12px

    .list-stats
        white-space: nowrap
        font-size: 13px
        .stats-scanned
            font-size: 18px
            font-weight: 700
            color: #67c23a
        .stats-sep, .stats-total
            color: #aaa
        .stats-label
            color: #aaa

    .list-search
        flex: 1
        ::v-deep
            .el-input__inner
                background: #1a1a1a
                border-color: #2a2a2a
                color: #ddd
                &::placeholder
                    color: #444
            .el-input__prefix
                color: #aaa

.list-loading
    padding: 20px 16px

.booking-items
    overflow-y: auto
    flex: 1
    min-height: 0

.booking-item
    display: flex
    align-items: center
    gap: 12px
    padding: 12px 16px
    border-bottom: 1px solid #1a1a1a
    transition: background .15s

    &:active
        background: #161616

    &.scanned
        opacity: 0.6

    .booking-avatar
        width: 40px
        height: 40px
        border-radius: 50%
        background: #1e3a5f
        color: $--color-primary
        display: flex
        align-items: center
        justify-content: center
        font-size: 14px
        font-weight: 700
        flex-shrink: 0
        &.scanned
            background: #1a3320
            color: #67c23a

    .booking-info
        flex: 1
        min-width: 0
        .booking-name
            font-size: 14px
            font-weight: 600
            color: #ddd
            white-space: nowrap
            overflow: hidden
            text-overflow: ellipsis
        .booking-meta
            font-size: 12px
            color: #aaa
            margin-top: 1px
            white-space: nowrap
            overflow: hidden
            text-overflow: ellipsis
        .booking-slot
            font-size: 11px
            color: #999
            margin-top: 2px

    .booking-action
        flex-shrink: 0
        display: flex
        flex-direction: column
        align-items: flex-end
        gap: 3px

        .scanned-time
            font-size: 10px
            color: #999
            white-space: nowrap

        .action-buttons
            display: flex
            align-items: center
            gap: 6px
            ::v-deep .el-button + .el-button
                margin-left: 0

        ::v-deep .el-button--mini
            padding: 5px 8px
            font-size: 11px

        // Mail-Button: etwas mehr horizontales Padding als grössere Touch-Fläche.
        ::v-deep .resend-mail-btn.el-button--mini
            padding-left: 14px
            padding-right: 14px

@keyframes pulse
    0%, 100%
        border-color: $--color-primary
    50%
        border-color: lighten(#409eff, 15%)
        box-shadow: 0 0 0 9999px rgba(0,0,0,0.35), 0 0 16px #409eff

@keyframes successFade
    from
        opacity: 0
    to
        opacity: 1

@keyframes checkPop
    0%
        transform: scale(0)
        opacity: 0
    60%
        transform: scale(1.12)
        opacity: 1
    100%
        transform: scale(1)
</style>
